"use client";
import { HTMLProps, useMemo, MouseEvent } from "react";
import Link, { LinkProps } from "next/link";
import { useParams } from "next/navigation";
import { Locales, locales } from "@/i18n/routing";
import { startPageTransition } from "@/utils/pageLoader.utils";
import { enablePrefetchByDefault } from "@/runtimeConfig/public";

export const CLink = ({
  href,
  children,
  onClick,
  target,
  prefetch = enablePrefetchByDefault,
  ...restProps
}: LinkProps & HTMLProps<HTMLAnchorElement>) => {
  // hooks
  const { locale } = useParams();

  // Normalize href if it's passed as an object containing url/href/link field from CMS
  const rawHref = useMemo(() => {
    if (!href) return "/";
    if (typeof href === "object" && href !== null) {
      const obj = href as Record<string, any>;
      if (!("pathname" in obj)) {
        if (typeof obj.url === "string") return obj.url;
        if (typeof obj.href === "string") return obj.href;
        if (typeof obj.link === "string") return obj.link;
      }
    }
    return href;
  }, [href]);

  // consts
  const isExternal = useMemo(() => {
    if (typeof rawHref !== "string") return false;
    return (
      /^(https?:)?\/\//.test(rawHref) ||
      /^[^:]+:.+$/.test(rawHref) ||
      (/^[\w-]+(\.[\w-]+)+/.test(rawHref) && !rawHref.startsWith("/"))
    );
  }, [rawHref]);

  const route = useMemo(() => {
    if (!rawHref) return "/";

    if (typeof rawHref === "string") {
      const hrefLocale = rawHref.split("/")[1] as Locales;
      const hrefAlreadyHasLocale = locales?.includes(hrefLocale);
      if (hrefAlreadyHasLocale) return rawHref;
      return `/${locale}${rawHref.startsWith("/") ? rawHref : `/${rawHref}`}`;
    }

    if (typeof rawHref === "object" && rawHref !== null) {
      const hrefObj = rawHref as Record<string, any>;
      const pathname: string = hrefObj.pathname || "";
      const hrefLocale = pathname.split("/")[1] as Locales;
      const hrefAlreadyHasLocale = locales?.includes(hrefLocale);

      if (hrefAlreadyHasLocale) return rawHref;

      const newPathname = `/${locale}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
      return {
        ...hrefObj,
        pathname: newPathname,
      };
    }

    return "/";
  }, [rawHref, locale]);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Avoid triggering for right-click or ctrl/cmd + click (open in new tab)
    if (
      event?.button === 0 && // Left click only
      !event?.metaKey && // Command key on Mac
      !event?.ctrlKey && // Ctrl key on Windows/Linux
      !event?.shiftKey && // Shift key (often for opening in a new window)
      !event?.altKey && // Alt key (rare use case for navigation)
      !target
    ) {
      startPageTransition();
    }
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <>
      <Link
        onClick={handleClick}
        {...restProps}
        href={isExternal ? (rawHref as LinkProps["href"]) : (route as LinkProps["href"])}
        target={isExternal ? "_blank" : "_self"}
        prefetch={prefetch}
      >
        {children}
      </Link>
    </>
  );
};
